import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { safeLocalStorage } from "../utils/safeLocalStorage";

type Theme = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

// Get the initial theme from localStorage or default to light
const getInitialTheme = (): Theme => {
  // Only access localStorage in browser environment
  if (typeof window === "undefined") {
    return "light"; // Default for SSR/prerendering
  }

  // Check localStorage first (safe for iOS Safari private mode)
  const storedTheme = safeLocalStorage.getItem("vitly-theme");
  if (storedTheme && ["light", "dark", "system"].includes(storedTheme)) {
    return storedTheme as Theme;
  }

  // Default to light mode for new users
  return "light";
};

// Resolve the actual theme based on user preference and system settings
const resolveTheme = (theme: Theme): ResolvedTheme => {
  if (typeof window === "undefined") {
    return "light"; // Default for SSR/prerendering
  }

  if (theme === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return theme;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(
    resolveTheme(theme),
  );
  const [isHydrated, setIsHydrated] = useState(false);

  // Apply theme class to document root
  const applyTheme = useCallback((resolvedTheme: ResolvedTheme) => {
    const root = document.documentElement;

    // Add transition class
    root.classList.add("theme-changing");

    // Update theme
    if (resolvedTheme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }

    // Remove transition class after animation
    setTimeout(() => {
      root.classList.remove("theme-changing");
    }, 50);
  }, []);

  // Set theme and persist to localStorage (safe for iOS Safari private mode)
  const setTheme = useCallback(
    (newTheme: Theme) => {
      setThemeState(newTheme);
      safeLocalStorage.setItem("vitly-theme", newTheme);
      const resolved = resolveTheme(newTheme);
      setResolvedTheme(resolved);
      applyTheme(resolved);
    },
    [applyTheme],
  );

  // Toggle between light and dark themes
  const toggleTheme = useCallback(() => {
    const newTheme = resolvedTheme === "dark" ? "light" : "dark";
    setTheme(newTheme);
  }, [resolvedTheme, setTheme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      if (theme === "system") {
        const resolved = e.matches ? "dark" : "light";
        setResolvedTheme(resolved);
        applyTheme(resolved);
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    // Legacy browsers
    else {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [theme, applyTheme]);

  // Initialize theme after hydration (runs once on mount)
  useEffect(() => {
    setIsHydrated(true);

    // Re-initialize theme from localStorage after hydration (safe for iOS Safari private mode)
    const storedTheme = safeLocalStorage.getItem("vitly-theme");
    if (
      storedTheme &&
      ["light", "dark", "system"].includes(storedTheme) &&
      storedTheme !== theme
    ) {
      const newTheme = storedTheme as Theme;
      setThemeState(newTheme);
      const resolved = resolveTheme(newTheme);
      setResolvedTheme(resolved);
      applyTheme(resolved);
    } else {
      // Apply current theme
      applyTheme(resolvedTheme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally runs once on mount for hydration
  }, []);

  // Apply theme on mount and when theme changes (only after hydration)
  useEffect(() => {
    if (isHydrated) {
      applyTheme(resolvedTheme);
    }
  }, [resolvedTheme, applyTheme, isHydrated]);

  return (
    <ThemeContext.Provider
      value={{ theme, resolvedTheme, setTheme, toggleTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
